import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Check, Download, Star, ChevronLeft, ChevronRight, Crown, RefreshCw } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PER_PAGE_LABELS = { 1: '1 — full page', 2: '2 — stacked', 4: '4 — grid', 6: '6 — grid', 9: '9 — classic' };
const PAGE_SIZE_LABELS = { letter: 'US Letter', a4: 'A4', square: 'Square (8.5″)' };
const THEME_LABELS = { event: "Your event's colours", classic: 'Classic Memouraa gold' };

export default function PhotoBookPage() {
  const { auraId } = useParams();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState([]); // photo ids in book order
  const [build, setBuild] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dragId = useRef(null);
  const poll = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/auras/${auraId}/photo-book`, { withCredentials: true });
      setData(d);
      setSettings(d.settings);
      setSelected(d.settings.media_ids === null ? d.photos.map((p) => p.id) : d.settings.media_ids);
      setBuild(d.build);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load the photo book.');
    }
  }, [auraId]);

  useEffect(() => { load(); return () => clearInterval(poll.current); }, [load]);

  const startPolling = useCallback(() => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const { data: b } = await axios.get(`${API_URL}/api/auras/${auraId}/photo-book/status`, { withCredentials: true });
        setBuild(b);
        if (b.status !== 'queued' && b.status !== 'building') clearInterval(poll.current);
      } catch {
        clearInterval(poll.current);
      }
    }, 3000);
  }, [auraId]);

  useEffect(() => {
    if (build && (build.status === 'queued' || build.status === 'building')) startPolling();
  }, [build?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !data) {
    return (
      <div className="min-h-screen bg-midnight"><SiteHeader />
        <div className="max-w-3xl mx-auto p-6 text-text-muted">{error}</div>
      </div>
    );
  }
  if (!data || !settings) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <div className="text-champagne animate-pulse font-serif text-xl">Loading…</div>
      </div>
    );
  }

  const allowed = data.allowed;
  const photosById = Object.fromEntries(data.photos.map((p) => [p.id, p]));
  const selectedSet = new Set(selected);
  const atCap = selected.length >= data.options.max_photos;

  const change = (patch) => { setSettings((s) => ({ ...s, ...patch })); setDirty(true); setSaved(false); };
  const changeSelection = (next) => { setSelected(next); setDirty(true); setSaved(false); };

  const toggle = (id) => {
    if (selectedSet.has(id)) {
      changeSelection(selected.filter((x) => x !== id));
      if (settings.cover_media_id === id) change({ cover_media_id: null });
    } else if (!atCap) {
      changeSelection([...selected, id]);
    }
  };
  const move = (id, delta) => {
    const i = selected.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= selected.length) return;
    const next = [...selected];
    [next[i], next[j]] = [next[j], next[i]];
    changeSelection(next);
  };
  const onDrop = (targetId) => {
    const from = dragId.current;
    dragId.current = null;
    if (from == null || from === targetId) return;
    const next = selected.filter((x) => x !== from);
    next.splice(next.indexOf(targetId), 0, from);
    changeSelection(next);
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const { data: r } = await axios.put(`${API_URL}/api/auras/${auraId}/photo-book`,
        { settings: { ...settings, media_ids: selected } }, { withCredentials: true });
      setSettings(r.settings);
      setSelected(r.settings.media_ids === null ? data.photos.map((p) => p.id) : r.settings.media_ids);
      setDirty(false); setSaved(true);
      return true;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const buildBook = async () => {
    if (dirty && !(await save())) return;
    setError('');
    try {
      const { data: b } = await axios.post(`${API_URL}/api/auras/${auraId}/photo-book/build`, {}, { withCredentials: true });
      setBuild(b);
      startPolling();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start the build.');
    }
  };

  const building = build && (build.status === 'queued' || build.status === 'building');
  const disabled = !allowed;

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />
      <div className="border-b border-champagne/10 px-4 py-3 sticky top-0 bg-midnight z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-2 flex-wrap">
          <Link to={`/aura/${auraId}`} className="text-text-muted hover:text-champagne flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to {data.aura.title}
          </Link>
          {allowed && (
            <div className="flex items-center gap-2">
              <button onClick={save} disabled={saving || !dirty} className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50" data-testid="photo-book-save">
                {saved && !dirty ? <><Check className="w-3 h-3" /> Saved</> : <><Save className="w-3 h-3" /> {saving ? 'Saving…' : 'Save'}</>}
              </button>
              <button onClick={buildBook} disabled={building || selected.length === 0} className="btn-champagne text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50" data-testid="photo-book-build">
                <RefreshCw className={`w-3 h-3 ${building ? 'animate-spin' : ''}`} /> {building ? 'Building…' : 'Build my book'}
              </button>
            </div>
          )}
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif text-text-light">Photo book</h1>
          <p className="text-text-muted text-sm mt-1">
            Choose the photos, their order, the cover and the layout. Build it whenever you like; your choices are also used for the book in your closing ceremony.
          </p>
        </div>

        {!allowed && (
          <div className="aura-card rounded-2xl p-6 border border-champagne/40" data-testid="photo-book-upgrade">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-champagne" />
              <h2 className="text-lg font-serif text-text-light">Design your own photo book</h2>
            </div>
            <p className="text-text-muted text-sm mb-4">{data.upgrade_message} Your event still gets an automatic photo book at its closing ceremony.</p>
            <Link to="/pricing" className="btn-champagne text-sm px-4 py-2 inline-block">View plans</Link>
          </div>
        )}

        {error && <div role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3">{error}</div>}

        {build && (build.status !== 'idle') && (
          <div className="aura-card rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" aria-live="polite" data-testid="photo-book-status">
            <div className="text-sm text-text-light">
              {building && 'Building your book… this usually takes under a minute.'}
              {build.status === 'ready' && `Your book is ready${build.built_at ? ` (built ${new Date(build.built_at).toLocaleString()})` : ''}.`}
              {build.status === 'failed' && (build.error || 'The build failed. Please try again.')}
            </div>
            {build.pdf_url && !building && (
              <a href={`${API_URL}${build.pdf_url}`} className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1" data-testid="photo-book-download">
                <Download className="w-3 h-3" /> Download PDF
              </a>
            )}
          </div>
        )}

        <fieldset disabled={disabled} className={disabled ? 'opacity-60' : ''}>
          <section className="aura-card rounded-2xl p-6 space-y-4">
            <h2 className="text-lg font-serif font-medium text-text-light">Cover &amp; text</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm text-text-muted block mb-2">Title</span>
                <input className="aura-input w-full" maxLength={120} placeholder={data.aura.title}
                  value={settings.title || ''} onChange={(e) => change({ title: e.target.value || null })} />
              </label>
              <label className="block">
                <span className="text-sm text-text-muted block mb-2">Subtitle</span>
                <input className="aura-input w-full" maxLength={160}
                  placeholder={[data.aura.event_date, data.aura.end_date].filter(Boolean).join(' – ') || 'e.g. the date or place'}
                  value={settings.subtitle || ''} onChange={(e) => change({ subtitle: e.target.value || null })} />
              </label>
            </div>
            <label className="block">
              <span className="text-sm text-text-muted block mb-2">Dedication page (optional)</span>
              <textarea className="aura-input w-full text-sm" rows={3} maxLength={500}
                placeholder="e.g. For everyone who danced with us"
                value={settings.dedication} onChange={(e) => change({ dedication: e.target.value })} />
            </label>
            <p className="text-xs text-text-muted">Cover photo: tap the star on any chosen photo below.{settings.cover_media_id ? '' : ' None chosen — the cover shows the title only.'}</p>
          </section>

          <section className="aura-card rounded-2xl p-6 space-y-4 mt-6">
            <h2 className="text-lg font-serif font-medium text-text-light">Layout</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <label className="block">
                <span className="text-sm text-text-muted block mb-2">Photos per page</span>
                <select className="aura-input w-full" value={settings.photos_per_page} onChange={(e) => change({ photos_per_page: Number(e.target.value) })}>
                  {data.options.photos_per_page.map((n) => <option key={n} value={n}>{PER_PAGE_LABELS[n] || n}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm text-text-muted block mb-2">Page size</span>
                <select className="aura-input w-full" value={settings.page_size} onChange={(e) => change({ page_size: e.target.value })}>
                  {data.options.page_size.map((k) => <option key={k} value={k}>{PAGE_SIZE_LABELS[k] || k}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm text-text-muted block mb-2">Colours</span>
                <select className="aura-input w-full" value={settings.theme} onChange={(e) => change({ theme: e.target.value })}>
                  {data.options.theme.map((k) => <option key={k} value={k}>{THEME_LABELS[k] || k}</option>)}
                </select>
              </label>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-text-light">
              <label className="flex items-center gap-2"><input type="checkbox" checked={settings.show_guest_names} onChange={(e) => change({ show_guest_names: e.target.checked })} /> Show who shared each photo</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={settings.include_notes} onChange={(e) => change({ include_notes: e.target.checked })} /> Include guest notes</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={settings.include_voice} onChange={(e) => change({ include_voice: e.target.checked })} /> Include voice toast transcripts</label>
            </div>
          </section>

          <section className="aura-card rounded-2xl p-6 mt-6">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h2 className="text-lg font-serif font-medium text-text-light">
                Photos <span className="text-text-muted text-sm font-sans">({selected.length} of {data.photos.length} in the book{atCap ? `, maximum ${data.options.max_photos}` : ''})</span>
              </h2>
              <div className="flex gap-2">
                <button type="button" className="btn-outline text-xs px-3 py-1.5" onClick={() => changeSelection(data.photos.slice(0, data.options.max_photos).map((p) => p.id))}>Select all</button>
                <button type="button" className="btn-outline text-xs px-3 py-1.5" onClick={() => { changeSelection([]); change({ cover_media_id: null }); }}>Clear</button>
              </div>
            </div>

            {data.photos.length === 0 ? (
              <p className="text-text-muted text-sm">No photos have been shared yet.</p>
            ) : (
              <>
                <h3 className="text-sm text-text-muted mb-2">In the book, in order — drag to reorder, or use the arrows</h3>
                <ol className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2 mb-6" data-testid="photo-book-order">
                  {selected.map((id, i) => photosById[id] && (
                    <li key={id} draggable={!disabled}
                      onDragStart={() => { dragId.current = id; }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDrop(id)}
                      className={`relative rounded-lg overflow-hidden border ${settings.cover_media_id === id ? 'border-champagne' : 'border-white/10'} bg-black/30`}>
                      <img src={`${API_URL}${photosById[id].url}`} alt={`${i + 1}. Shared by ${photosById[id].guest_name || 'a guest'}`} loading="lazy" className="w-full aspect-square object-cover" />
                      <span className="absolute top-1 left-1 text-[11px] bg-black/70 text-white rounded px-1.5">{i + 1}</span>
                      <button type="button" onClick={() => change({ cover_media_id: settings.cover_media_id === id ? null : id })}
                        className="absolute top-1 right-1 p-1.5 rounded bg-black/70" aria-pressed={settings.cover_media_id === id}
                        aria-label={settings.cover_media_id === id ? 'Remove as cover photo' : 'Use as cover photo'}>
                        <Star className={`w-4 h-4 ${settings.cover_media_id === id ? 'text-champagne fill-current' : 'text-white'}`} />
                      </button>
                      <div className="absolute bottom-0 inset-x-0 flex justify-between bg-black/70">
                        <button type="button" onClick={() => move(id, -1)} disabled={i === 0} className="p-2 text-white disabled:opacity-30" aria-label="Move earlier"><ChevronLeft className="w-4 h-4" /></button>
                        <button type="button" onClick={() => toggle(id)} className="text-[11px] text-white px-1" aria-label="Remove from book">Remove</button>
                        <button type="button" onClick={() => move(id, 1)} disabled={i === selected.length - 1} className="p-2 text-white disabled:opacity-30" aria-label="Move later"><ChevronRight className="w-4 h-4" /></button>
                      </div>
                    </li>
                  ))}
                </ol>

                {data.photos.some((p) => !selectedSet.has(p.id)) && (
                  <>
                    <h3 className="text-sm text-text-muted mb-2">Not in the book — tap to add</h3>
                    <ul className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2" data-testid="photo-book-available">
                      {data.photos.filter((p) => !selectedSet.has(p.id)).map((p) => (
                        <li key={p.id}>
                          <button type="button" onClick={() => toggle(p.id)} disabled={atCap}
                            className="block w-full rounded-lg overflow-hidden border border-white/10 opacity-70 hover:opacity-100 disabled:opacity-30"
                            aria-label={`Add photo by ${p.guest_name || 'a guest'}`}>
                            <img src={`${API_URL}${p.url}`} alt="" loading="lazy" className="w-full aspect-square object-cover" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </section>
        </fieldset>
      </main>
    </div>
  );
}
